import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
// Custom Components
import VirtualScrollTable from "../../components/ReactTable/VirtualScrollTable";
import { getSortString } from "../../utils/tableUtils";
import { useGroupUsers } from "../../hooks/apiHooks/groupUsers";
import useScopedTranslation from "../../hooks/useScopedTranslation";
import { useVirtualScrolling } from "../../components/ReactTable/useVirtualScrolling";

import { formatDateInLocalTz } from "../../utils/datetime";

const pageSize = 25;

const GroupMembersTable = ({ groupId, maxBodyHeight }) => {
  const columnT = useScopedTranslation("table.columnHeader");
  const localT = useScopedTranslation("manageGroupMembers");
  const [tableState, setTableState] = useState({ page: 1, sortBy: "" });
  const { sortBy, page } = tableState;
  const { getGroupUsers } = useGroupUsers();

  const fetchData = useCallback(() => {
    return getGroupUsers({
      page,
      pageSize,
      filters: {
        active: true,
        groupId: [groupId]
      },
      fields: {
        groupsUser: ["extendedSourceType", "createdAt", "user", "creator"],
        user: ["fullName", "positionName", "locationName"],
        creator: ["fullName"]
      },
      include: ["user", "user.location", "user.position", "creator"],
      sort: "name"
    });
  }, [tableState]);

  const {
    results: employees,
    hasNextPage,
    loadingFirstPage
  } = useVirtualScrolling({
    fetchData,
    page
  });

  const buildCommentString = (type, creator, createdAt) => {
    if (type === "employee") {
      return localT("addedByPerson", {
        name: creator.fullName,
        date: formatDateInLocalTz(createdAt)
      });
    } else {
      return localT(type);
    }
  };

  const usersColumns = [
    {
      Header: columnT("name"),
      accessor: "user.fullName",
      id: "userName",
      disableSortBy: true
    },
    {
      Header: columnT("position"),
      accessor: "user.positionName",
      id: "positionName",
      disableSortBy: true
    },
    {
      Header: columnT("location"),
      accessor: "user.locationName",
      id: "locationName",
      disableSortBy: true
    },
    {
      Header: columnT("comments"),
      accessor: row =>
        buildCommentString(
          row.extendedSourceType,
          row.creator || {},
          row.createdAt
        ),
      id: "comments",
      disableSortBy: true
    }
  ];

  const handleTableStateChange = useCallback(
    newState => {
      const newSort = getSortString(newState.sortBy[0]);
      const newPage = newState.pageIndex;
      if (newSort !== sortBy || newPage !== page) {
        setTableState({ sortBy: newSort, page: newState.pageIndex });
      }
    },
    [sortBy, page]
  );

  return (
    <VirtualScrollTable
      columns={usersColumns}
      data={employees || []}
      loading={loadingFirstPage}
      totalCount={hasNextPage ? employees.length + 1 : employees.length}
      pageSize={pageSize}
      tableHeight={maxBodyHeight}
      onStateChange={handleTableStateChange}
    />
  );
};

GroupMembersTable.propTypes = {
  groupId: PropTypes.string,
  maxBodyHeight: PropTypes.number
};

export default GroupMembersTable;
